import { getColors } from '../../charts/shared/palettes';
import { J, JN, LEGEND_SPACING, TITLE_SPACING, fileHeader } from './shared';

export function genScatter(chartType, config, t, data) {
  const groups = {};
  data.forEach(pt => {
    const cat = pt.category || 'Data';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push({ x: pt.x, y: pt.y });
  });

  const categories = Object.keys(groups);
  const baseColors = getColors(config, categories.length, t);
  const catColors = Object.fromEntries(categories.map((cat, i) => [cat, baseColors[i % baseColors.length]]));

  const xVals = data.map(d => d.x);
  const yVals = data.map(d => d.y);
  const xMin = (config.xMin && String(config.xMin).trim() !== '') ? parseFloat(config.xMin) : Math.floor(Math.min(...xVals) * 0.9);
  const xMax = (config.xMax && String(config.xMax).trim() !== '') ? parseFloat(config.xMax) : Math.ceil(Math.max(...xVals) * 1.1);
  const yMin = (config.yMin && String(config.yMin).trim() !== '') ? parseFloat(config.yMin) : Math.floor(Math.min(...yVals) * 0.9);
  const yMax = (config.yMax && String(config.yMax).trim() !== '') ? parseFloat(config.yMax) : Math.ceil(Math.max(...yVals) * 1.1);

  const showGrid = !!config.showGrid;
  const showLegend = !!config.showLegend;
  const hasTitle = !!(config.title && String(config.title).trim());
  const markerSize = config.markerSize || 100;
  const markerAlpha = config.markerAlpha || 1.0;
  const markerRadius = Math.sqrt(markerSize / Math.PI);
  const legendPos = config.legendPosition || 'top';
  const marginTop = hasTitle ? 40 : (showLegend && legendPos === 'top' ? 40 : 20);
  const marginRight = showLegend && legendPos === 'right' ? 150 : 80;
  const marginBottom = showLegend && legendPos === 'bottom' ? 80 : 60;
  const titleStr = hasTitle ? String(config.title).replace(/</g, '&lt;') : '';
  const titleBlock = hasTitle ? `\n      <div style={{ position: 'absolute', top: ${TITLE_SPACING.topInset}, left: 70, fontSize: 18, fontWeight: 400, color: THEME.titleColor }}>${titleStr}</div>` : '';

  const LIW = 110;
  const gridBlock = showGrid
    ? `          <GridRows scale={yScale} width={innerWidth} stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} strokeDasharray="3 3" />\n          <GridColumns scale={xScale} height={innerHeight} stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} strokeDasharray="3 3" />\n`
    : '';

  const legendTopBlock = (showLegend && legendPos === 'top')
    ? `          <g transform={'translate(' + (innerWidth / 2) + ', -${LEGEND_SPACING.topOffset})'}>
            {CATEGORIES.map((cat, i) => {
              const startX = -(CATEGORIES.length * ${LIW}) / 2 + i * ${LIW};
              return (
                <g key={cat} transform={'translate(' + startX + ', 0)'}>
                  <circle cx={${Math.round(LEGEND_SPACING.markerSize)}} cy={0} r={${LEGEND_SPACING.markerSize}} fill={CATEGORY_COLORS[cat]} />
                  <text x={${LEGEND_SPACING.markerTextX}} y={${LEGEND_SPACING.textDy}} fill={THEME.legendTextColor} fontSize={11}>{cat}</text>
                </g>
              );
            })}
          </g>\n`
    : '';

  const legendBottomBlock = (showLegend && legendPos === 'bottom')
    ? `          <g transform={'translate(' + (innerWidth / 2) + ', ' + (innerHeight + ${LEGEND_SPACING.bottomGap}) + ')'}>
            {CATEGORIES.map((cat, i) => {
              const startX = -(CATEGORIES.length * ${LIW}) / 2 + i * ${LIW};
              return (
                <g key={cat} transform={'translate(' + startX + ', 0)'}>
                  <circle cx={${Math.round(LEGEND_SPACING.markerSize)}} cy={0} r={${LEGEND_SPACING.markerSize}} fill={CATEGORY_COLORS[cat]} />
                  <text x={${LEGEND_SPACING.markerTextX}} y={${LEGEND_SPACING.textDy}} fill={THEME.legendTextColor} fontSize={11}>{cat}</text>
                </g>
              );
            })}
          </g>\n`
    : '';

  const legendRightBlock = (showLegend && legendPos === 'right')
    ? `        <g transform={'translate(' + (70 + innerWidth + ${LEGEND_SPACING.rightGap}) + ', ' + (${marginTop} + innerHeight / 2 - CATEGORIES.length * 12) + ')'}>
          {CATEGORIES.map((cat, i) => (
            <g key={cat} transform={'translate(0, ' + (i * ${LEGEND_SPACING.rightItemHeight}) + ')'}>
              <circle cx={${Math.round(LEGEND_SPACING.markerSize)}} cy={0} r={${LEGEND_SPACING.markerSize}} fill={CATEGORY_COLORS[cat]} />
              <text x={${LEGEND_SPACING.markerTextX}} y={${LEGEND_SPACING.textDy}} fill={THEME.legendTextColor} fontSize={11}>{cat}</text>
            </g>
          ))}
        </g>\n`
    : '';

  return (
    fileHeader(['@visx/shape', '@visx/scale', '@visx/axis', '@visx/grid', '@visx/group'], 'scatter-plot') +
    `import React, { useMemo } from 'react';
import { Circle } from '@visx/shape';
import { scaleLinear } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows, GridColumns } from '@visx/grid';
import { Group } from '@visx/group';

const GROUPED_DATA = ${JN(groups)};
const CATEGORIES = ${JN(categories)};
const CATEGORY_COLORS = ${JN(catColors)};
const THEME = ${JN(t)};
const X_DOMAIN = [${xMin}, ${xMax}];
const Y_DOMAIN = [${yMin}, ${yMax}];
const MARKER_RADIUS = ${markerRadius.toFixed(3)};
const MARKER_ALPHA = ${markerAlpha};

export default function Chart({ width = 720, height = 480 }) {
  const marginLeft = 70;
  const marginTop = ${marginTop};
  const marginRight = ${marginRight};
  const marginBottom = ${marginBottom};
  const innerWidth = Math.max(0, width - marginLeft - marginRight);
  const innerHeight = Math.max(0, height - marginTop - marginBottom);

  const xScale = useMemo(() => scaleLinear({ domain: X_DOMAIN, range: [0, innerWidth] }), [innerWidth]);
  const yScale = useMemo(() => scaleLinear({ domain: Y_DOMAIN, range: [innerHeight, 0] }), [innerHeight]);

  return (
    <div style={{ width, height, position: 'relative', backgroundColor: THEME.background }}>${titleBlock}
      <svg width={width} height={height}>
        <Group left={marginLeft} top={marginTop}>
${gridBlock}          {Object.entries(GROUPED_DATA).map(([category, points]) =>
            points.map((point, i) => (
              <Circle
                key={category + '-' + i}
                cx={xScale(point.x)}
                cy={yScale(point.y)}
                r={MARKER_RADIUS}
                fill={CATEGORY_COLORS[category]}
                fillOpacity={MARKER_ALPHA}
                stroke={THEME.axisColor}
                strokeWidth={1}
              />
            ))
          )}
          <AxisBottom
            scale={xScale}
            top={innerHeight}
            stroke={THEME.axisColor}
            strokeWidth={THEME.spineWidth}
            tickStroke={THEME.axisColor}
            tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, textAnchor: 'middle', fontFamily: THEME.fontBody })}
            label={${J(config.xlabel || '')}}
            labelProps={{ fill: THEME.labelColor, fontSize: 14, textAnchor: 'middle', fontFamily: THEME.fontBody }}
            labelOffset={20}
          />
          <AxisLeft
            scale={yScale}
            stroke={THEME.axisColor}
            strokeWidth={THEME.spineWidth}
            tickStroke={THEME.axisColor}
            tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, textAnchor: 'end', dx: '-0.25em', dy: '0.33em', fontFamily: THEME.fontBody })}
            label={${J(config.ylabel || '')}}
            labelProps={{ fill: THEME.labelColor, fontSize: 14, textAnchor: 'middle', fontFamily: THEME.fontBody }}
            labelOffset={45}
          />
${legendTopBlock}${legendBottomBlock}        </Group>
${legendRightBlock}      </svg>
    </div>
  );
}
`
  );
}
